import React, { useEffect, useState } from 'react'
import { Pagination, Payload, Result, SearchSpringProductResponse } from '../../search-spring/types/types'
import InstantSearchGrid from '../../search-spring/components/InstantSearchGrid'
import { convertSearchSpringProductPricingToMarketPrice, isSearchSpringProductResponse, standardSearch } from '../../helpers/searchSpringHelpers'
import PaginationBar from './PaginationBar'
import CollectionSearchGrid from '../../search-spring-collection/components/CollectionSearchGrid'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'

type Props = {
  pagination: Pagination
  products: Result[]
  searchQuery: string
  payload: Payload,
  isSearchPage?: boolean
  collectionExtraProductBuffer?: number
}

declare var Shopify: { currency: { active: string } }
const updatePageUrl = (pageNumber: number | null) => {
  if (!pageNumber) return
  const url = new URL(window.location.href)
  url.searchParams.set('page', pageNumber.toString())
  window.history.pushState({}, '', url)
}

const PaginationService = ({pagination, products, searchQuery, payload, isSearchPage = true, collectionExtraProductBuffer = 0}: Props) => {
  const [searchPageProducts, setSearchPageProducts] = useState<Result[]>(products)
  const [nextPage, setNextPage] = useState<number | null>(pagination.nextPage)
  const [currentProductCount, setCurrentProductCount] = useState<number>(pagination.end)
  const [error, setError] = useState(false)
  const [carryoverProducts, setCarryoverProducts] = useState<Result[]>([])
  const isCollectionSearch = isSearchPage == false ? true : false
  const windowSize = useWindowSize()
  const baseResultsPerPage = windowSize.width <= 640 ? CD_STATE.searchspring.numberOfProductsMobile : CD_STATE.searchspring.numberOfProductsDesktop
  // TODO: handle push state, pop state here

  // Load products on page taking into account disrupter grid carryover
  useEffect(() => {
    const currentPage = pagination.currentPage ?? 1

    if (!isCollectionSearch || collectionExtraProductBuffer <= 0 || currentPage <= 1 || !pagination.nextPage) {
      setSearchPageProducts(products)
      setCarryoverProducts([])
      setCurrentProductCount(products.length)
      return
    }

    const totalProducts = products.length
    const carryoverCount = currentPage % 2 === 0 ? collectionExtraProductBuffer : 0
    const visibleCount = totalProducts - carryoverCount

    setSearchPageProducts(products.slice(0, visibleCount))
    setCarryoverProducts(products.slice(visibleCount))
    setCurrentProductCount(visibleCount)
  }, [products, pagination.currentPage, pagination.nextPage, isCollectionSearch, collectionExtraProductBuffer])

  const loadMoreProducts = async () => {
    const updatedPayload: Payload = {
      ...payload
    }
    if (isCollectionSearch) {
      updatedPayload.resultsPerPage = baseResultsPerPage + collectionExtraProductBuffer
    }
    if (nextPage) {
      updatedPayload.page = nextPage
    }
    const pageNumber = updatedPayload.page ?? 1

    try {
      const response: SearchSpringProductResponse | unknown = await standardSearch({searchQuery: searchQuery, payload: updatedPayload, searchType: isCollectionSearch ? 'collection' : 'search' })          
      if (isSearchSpringProductResponse(response)) {
        let newProducts = response.results
        if (Shopify.currency.active != "AUD") {
          const newproductData: SearchSpringProductResponse = await convertSearchSpringProductPricingToMarketPrice(response)
          // Incase the product conversion fails just put in the AU price, the PDP and cart will show correct prices.
          newProducts = newproductData.results ? newproductData.results : response.results
        }

        if (isCollectionSearch && collectionExtraProductBuffer > 0 && pageNumber > 1) {
          const mergedProducts = [...carryoverProducts, ...newProducts]
          const visibleProducts = mergedProducts.length - (mergedProducts.length % 4)
          const pageProducts = mergedProducts.slice(0, visibleProducts)
          const nextCarryover = mergedProducts.slice(visibleProducts)

          if (!response.pagination.nextPage) {
            setCarryoverProducts([])
            setSearchPageProducts(prev => prev = [...prev, ...pageProducts, ...nextCarryover])
            setCurrentProductCount(prev => prev + pageProducts.length + nextCarryover.length)
          } else {
            setCarryoverProducts(nextCarryover)
            setSearchPageProducts(prev => prev = [...prev, ...pageProducts])
            setCurrentProductCount(prev => prev + pageProducts.length)
          }
        } else {
          setCarryoverProducts([])
          setSearchPageProducts(prev => prev = [...prev, ...newProducts])
          setCurrentProductCount(prev => prev = response.pagination.end)
        }

        response.pagination.nextPage ? setNextPage(prev => prev = response.pagination.nextPage) : setNextPage(null)
        
        requestAnimationFrame(() => {
          // Updating page param.
          updatePageUrl(updatedPayload.page || null)
          window.scrollTo(0, scrollY)
        })
      } else {
        setError(true)
        throw new Error 
      } 
    } catch (error) {
      setError(true)
    }
  }

  const ComponentHandler = (): JSX.Element => {
    if (error) {
      return <div className="text-xl font-bstSpyre mt-4 text-center mx-5">Sorry, there was an error getting more products. Please try again later or contact us if this issue persists.</div>
    }
    return <>
      {searchPageProducts.length > 0 ? (
        isCollectionSearch ? (
          <CollectionSearchGrid products={searchPageProducts} />
        ) : (
          <InstantSearchGrid products={searchPageProducts} />
        )
      ) : (
        <div className="text-xl font-bstSpyre my-4 text-center mx-5">
          If you are not seeing any results, try removing some of your selected filters.
        </div>
      )}
      
      {nextPage ? (
        <div className='flex flex-col items-center my-LG md:my-DLG'>
          <div className='font-calibre text-sm-b uppercase text-black'>{`${currentProductCount} of ${pagination.totalResults}`}</div>
          
          <div className='mb-SM md:mb-DSM'>
          <PaginationBar currentProductCount={currentProductCount} totalResults={pagination.totalResults} maxWidth={210} tailwindClass='mt-2'/>
          </div>

          <button onClick={loadMoreProducts} className="Button Button--secondary-dark uppercase backdrop-filter-none min-w-[150px] h-[35px] md:h-[44px] font-calibre text-sm-b text-black hover:text-white focus:text-[var(--ink)]">Load More</button>
        </div>
      ) : null}

      {(!nextPage && CD_STATE.nextCollection) ? (
        <div className='flex justify-center my-LG md:my-DLG'>
          <a href={CD_STATE.nextCollection.url} className="Button Button--secondary-dark block min-w-[150px] h-[35px] md:h-[44px] font-calibre text-sm-b text-black hover:text-white focus:text-[var(--ink)] uppercase backdrop-filter-none">
            {CD_STATE.nextCollection.title}
          </a>
        </div>
      ) : null}
    </>
  }

  return (
    <ComponentHandler />
  )
}

export default PaginationService
